/**
 * Auth Store
 * @typedef {Object} AuthStore
 * @property {boolean} authenticated - Indicates if the user is authenticated.
 * @property {Object} user - User object containing user information.
 * @property {boolean} loadingProfile - Indicates if the user profile is being loaded.
 * @property {string} token - User token.
 * @property {string} refreshToken - Refresh token.
 * @property {number|null} tokenExpires - Expiration time of the user token.
 * @property {number|null} refreshTokenExpires - Expiration time of the refresh token.
 */

/**
 * Auth Store
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import keycloakService from '@services/keycloakService';
import { jwtDecode } from 'jwt-decode';
import { getUserProfile } from '@/services/contactLambda';
import SecureLS from 'secure-ls';
import { BASE_STORAGE_KEY, ORGANIZATION_TYPES } from '@/utils/constants';
import logger from '@/utils/logger';

// Create an instance of SecureLS with encryption enabled
const ls = new SecureLS({ encodingType: 'aes' });

export const useAuthStore = defineStore('authStore', () => {
    // ===============================
    // STATE
    // ===============================
    const authenticated = ref(false);
    const user = ref(null);
    const loadingProfile = ref(false);
    const profileLoaded = ref(false);
    const token = ref('');
    const refreshToken = ref('');
    const tokenExpires = ref(null);
    const refreshTokenExpires = ref(null);
    const primaryOrganizationChanged = ref(false);

    // ===============================
    // ACTIONS
    // ===============================
    
    /**
     * Initializes OAuth with Keycloak
     * @param {Object} keycloak - Keycloak instance
     */
    async function initOauth(keycloak) {
        if (loadingProfile.value) {
            return;
        }

        if (keycloak.authenticated) {
            loadingProfile.value = true;
            authenticated.value = keycloak.authenticated;
            if (keycloak.tokenParsed && keycloak.tokenParsed.exp) {
                tokenExpires.value = keycloak.tokenParsed.exp;
            }

            const decodedToken = jwtDecode(keycloak.refreshToken);
            if (decodedToken && decodedToken.exp) {
                refreshTokenExpires.value = decodedToken.exp;
            }

            token.value = keycloak.token;
            refreshToken.value = keycloak.refreshToken;

            if (user.value == null || !profileLoaded.value) {
                try {
                    const userProfile = await keycloakService.GetUserProfile();
                    user.value = userProfile;
                    const dbProfile = await getUserProfile();
                    const orgTypes = dbProfile.primaryOrganizationTypes ? dbProfile.primaryOrganizationTypes.split(',') : [];
                    user.value.firstName = dbProfile.firstName;
                    user.value.lastName = dbProfile.lastName;
                    user.value.primaryOrganizationId = dbProfile.primaryOrganizationId;
                    user.value.primaryOrganizationName = dbProfile.primaryOrganizationName;
                    user.value.primaryOrganizationTypes = orgTypes;
                    user.value.isTpaUser = orgTypes.includes(ORGANIZATION_TYPES.TPA);
                    user.value.isHceOnlyUser = orgTypes.includes(ORGANIZATION_TYPES.HCE) && orgTypes.length === 1;
                    primaryOrganizationChanged.value = false;
                    profileLoaded.value = true;
                } catch (error) {
                    logger.error(error);
                    keycloakService.CallLogout();
                    clearUserData();
                } finally {
                    loadingProfile.value = false;
                    clearHepLocalStorage();
                }
            } else {
                profileLoaded.value = true;
                loadingProfile.value = false;
            }
        } else {
            clearUserData();
            loadingProfile.value = false;
            profileLoaded.value = true;
        }
    }

    /**
     * Logs in the user
     * @param {string} redirectUri - URI to redirect after login
     */
    async function login(redirectUri) {
        try {
            await keycloakService.CallLogin(redirectUri);
            clearUserData();
            initOauth(keycloakService.keycloak);
        } catch (error) {
            logger.error(error);
        }
    }

    /**
     * Logs out the user
     */
    async function logout() {
        try {
            clearUserData();
            await keycloakService.CallLogout();
        } catch (error) {
            logger.error(error);
        }
    }

    /**
     * Refreshes the user token
     */
    async function refreshUserToken() {
        try {
            const keycloak = await keycloakService.CallTokenRefresh();
            await initOauth(keycloak);
        } catch (error) {
            logger.error(error);
        }
    }

    /**
     * Checks if token is expired and refreshes if needed
     */
    async function checkTokenExpiration() {
        if (isTokenExpired()) {
            await refreshUserToken();
        }
    }

    /**
     * Checks if the token is expired
     * @returns {boolean} True if token is expired
     */
    function isTokenExpired() {
        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds since the epoch
        return currentTime >= tokenExpires.value;
    }

    /**
     * Checks if the refresh token is expired
     * @returns {boolean} True if refresh token is expired
     */
    function isRefreshTokenExpired() {
        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds since the epoch
        return currentTime >= refreshTokenExpires.value;
    }

    /**
     * Refreshes the user profile from the database
     */
    async function refreshUserProfile() {
        const dbProfile = await getUserProfile();
        const orgTypes = dbProfile.primaryOrganizationTypes ? dbProfile.primaryOrganizationTypes.split(',') : [];
        user.value.primaryOrganizationId = dbProfile.primaryOrganizationId;
        user.value.primaryOrganizationName = dbProfile.primaryOrganizationName;
        user.value.primaryOrganizationTypes = orgTypes;
        primaryOrganizationChanged.value = true;
    }

    /**
     * Clears user data from the store
     */
    function clearUserData() {
        authenticated.value = false;
        user.value = null;
        profileLoaded.value = false;
        token.value = '';
        refreshToken.value = '';
        clearHepLocalStorage();
    }

    /**
     * Clears HEP-related data from localStorage and sessionStorage
     */
    function clearHepLocalStorage() {
        // remove keys stored via SecureLS and any raw localStorage keys starting with 'hep'
        try {
            Object.keys(localStorage).forEach((k) => {
                if (!k) return;
                if (k.startsWith(BASE_STORAGE_KEY)) {
                    try { ls.remove(k); } catch (e) { /* ignore */ }
                    try { localStorage.removeItem(k); } catch (e) { /* ignore */ }
                    try { sessionStorage.removeItem(k); } catch (e) { /* ignore */ }
                }

                if (k.includes('appStore')) {
                    try { ls.remove(k); } catch (e) { /* ignore */ }
                    try { localStorage.removeItem(k); } catch (e) { /* ignore */ }                           
                }
            });

            Object.keys(sessionStorage).forEach((k) => {
                if (k && k.startsWith(BASE_STORAGE_KEY)) {
                    try { sessionStorage.removeItem(k); } catch (e) { /* ignore */ }
                }
            });
        } catch (err) {
            // fallback: iterate by index if Object.keys failed
            for (let i = localStorage.length - 1; i >= 0; i--) {
                const key = localStorage.key(i);
                if (key && key.startsWith(BASE_STORAGE_KEY)) {
                    try { ls.remove(key); } catch (e) { /* empty */ }
                    try { localStorage.removeItem(key); } catch (e) { /* empty */ }
                }

                if (key && key.includes('appStore')) {
                    try { ls.remove(key); } catch (e) { /* empty */ }   
                    try { localStorage.removeItem(key); } catch (e) { /* empty */ }
                }
            }
            
            for (let i = sessionStorage.length - 1; i >= 0; i--) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith(BASE_STORAGE_KEY)) {
                    try { sessionStorage.removeItem(key); } catch (e) { /* empty */ }
                }
            }
        }
    }

    // ===============================
    // RETURN (PUBLIC API)
    // ===============================
    return {
        // State
        authenticated,
        user,
        loadingProfile,
        profileLoaded,
        token,
        refreshToken,
        tokenExpires,
        refreshTokenExpires,
        primaryOrganizationChanged,
        
        // Actions
        initOauth,
        login,
        logout,
        refreshUserToken,
        checkTokenExpiration,
        isTokenExpired,
        isRefreshTokenExpired,
        refreshUserProfile,
        clearUserData,
        clearHepLocalStorage,
    };
}, {
    persist: {
        storage: {
            getItem: (key) => {
                const data = ls.get(key);
                return data ? JSON.parse(data) : null;
            },
            setItem: (key, value) => {
                ls.set(key, JSON.stringify(value));
            },
            removeItem: (key) => {
                ls.remove(key);
            },
        },
    },
});

